
import { 
  MessageSquare, 
  Globe, 
  Shield, 
  CreditCard, 
  Rocket, 
  Sparkles, 
  Zap, 
  BarChart,
  Star,
  Award
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";

const features = [
  {
    id: 1,
    title: "Création de Contenu par IA",
    description:
      "Générez du contenu engageant pour toutes les plateformes avec notre assistant IA avancé.",
    icon: <Sparkles className="h-8 w-8" />,
    color: "from-violet-500 to-purple-600",
    lightColor: "from-violet-200 to-purple-300",
    textColor: "text-violet-500",
    hoverTextColor: "group-hover:text-white",
    shadowColor: "shadow-violet-200/60",
    mainTag: "IA"
  },
  {
    id: 2,
    title: "Gestion Multi-Plateformes",
    description:
      "Gérez tous vos comptes sociaux depuis un tableau de bord unique et intuitif.",
    icon: <Globe className="h-8 w-8" />,
    color: "from-blue-500 to-cyan-600",
    lightColor: "from-blue-200 to-cyan-300",
    textColor: "text-blue-500",
    hoverTextColor: "group-hover:text-white",
    shadowColor: "shadow-blue-200/60",
    mainTag: "Intégration"
  },
  {
    id: 3,
    title: "Modération Intelligente",
    description:
      "Notre IA filtre automatiquement les commentaires inappropriés et met en évidence les opportunités d'engagement.",
    icon: <Shield className="h-8 w-8" />,
    color: "from-emerald-500 to-green-600",
    lightColor: "from-emerald-200 to-green-300",
    textColor: "text-emerald-500",
    hoverTextColor: "group-hover:text-white",
    shadowColor: "shadow-emerald-200/60",
    mainTag: "Sécurité"
  },
  {
    id: 4,
    title: "Système de Crédits Flexible",
    description:
      "Notre système de crédits vous permet de ne payer que ce que vous utilisez sans engagement.",
    icon: <CreditCard className="h-8 w-8" />,
    color: "from-orange-500 to-amber-600",
    lightColor: "from-orange-200 to-amber-300",
    textColor: "text-orange-500",
    hoverTextColor: "group-hover:text-white",
    shadowColor: "shadow-orange-200/60",
    mainTag: "Économie"
  },
  {
    id: 5,
    title: "Analyse Détaillée",
    description:
      "Visualisez vos performances avec des graphiques intuitifs et des indicateurs clés personnalisables.",
    icon: <BarChart className="h-8 w-8" />,
    color: "from-fuchsia-500 to-pink-600",
    lightColor: "from-fuchsia-200 to-pink-300",
    textColor: "text-fuchsia-500",
    hoverTextColor: "group-hover:text-white",
    shadowColor: "shadow-fuchsia-200/60",
    mainTag: "Insights"
  },
  {
    id: 6,
    title: "Vitesse d'Exécution",
    description:
      "Optimisez votre flux de travail grâce à une interface ultra-rapide et des automatisations intelligentes.",
    icon: <Zap className="h-8 w-8" />,
    color: "from-yellow-500 to-amber-600",
    lightColor: "from-yellow-200 to-amber-300",
    textColor: "text-yellow-500",
    hoverTextColor: "group-hover:text-white",
    shadowColor: "shadow-yellow-200/60",
    mainTag: "Performance"
  },
  {
    id: 7,
    title: "Récompenses Utilisateurs",
    description:
      "Programme de fidélité qui récompense vos actions et votre engagement sur la plateforme.",
    icon: <Award className="h-8 w-8" />,
    color: "from-rose-500 to-red-600",
    lightColor: "from-rose-200 to-red-300",
    textColor: "text-rose-500",
    hoverTextColor: "group-hover:text-white",
    shadowColor: "shadow-rose-200/60",
    mainTag: "Fidélité"
  },
  {
    id: 8,
    title: "Contenu Premium",
    description:
      "Accédez à des modèles et ressources exclusifs créés par des experts du marketing digital.",
    icon: <Star className="h-8 w-8" />,
    color: "from-amber-500 to-yellow-600",
    lightColor: "from-amber-200 to-yellow-300",
    textColor: "text-amber-500",
    hoverTextColor: "group-hover:text-white",
    shadowColor: "shadow-amber-200/60",
    mainTag: "Exclusivité"
  },
];

const FeaturesSection = () => {
  return (
    <section id="features" className="py-24 bg-gradient-to-b from-white via-indigo-50/20 to-white">
      <div className="container px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center mb-16 animate-fade-in">
          <div className="inline-flex items-center justify-center px-4 py-1.5 mb-6 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 text-white text-sm font-medium relative overflow-hidden group">
            <div className="absolute inset-0 w-3 bg-white/20 skew-x-[-20deg] group-hover:animate-[shine_1s_ease-in-out_infinite] opacity-0 group-hover:opacity-100 transition-opacity"></div>
            <Rocket className="h-4 w-4 mr-2" />
            <span>Fonctionnalités innovantes</span>
          </div>
          <h2 className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl lg:text-5xl bg-clip-text text-transparent bg-gradient-to-r from-indigo-700 via-purple-700 to-indigo-700">
            Tout ce dont vous avez besoin pour réussir
          </h2>
          <p className="mt-4 text-lg text-gray-600 lg:text-xl">
            Scaloo combine des outils puissants et une conception intuitive pour vous donner un contrôle total sur votre présence sociale.
          </p>
        </div>

        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4 mt-12">
          {features.map((feature) => (
            <Card 
              key={feature.id} 
              className="group relative overflow-hidden border-0 bg-white hover:bg-gradient-to-br hover:from-gray-50 hover:to-white transition-all duration-500 shadow-lg hover:shadow-2xl rounded-3xl"
            >
              <div className="absolute -inset-1 bg-gradient-to-br opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl group-hover:blur-md rounded-3xl"></div>
              
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              <div className="relative z-10 p-6 sm:p-8 h-full flex flex-col">
                <div className="flex items-center justify-between mb-6">
                  <div className={`flex-shrink-0 inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br ${feature.color} text-white p-3 shadow-lg ${feature.shadowColor}`}>
                    {feature.icon}
                  </div>
                  
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full bg-gradient-to-br ${feature.lightColor} ${feature.textColor}`}>
                    {feature.mainTag}
                  </span>
                </div>
                
                <h3 className={`mb-3 text-xl font-bold transition-colors ${feature.textColor} ${feature.hoverTextColor} group-hover:translate-x-1 transition-transform duration-300`}>
                  {feature.title}
                </h3>
                
                <p className="text-gray-600 group-hover:text-gray-700 transition-colors duration-300 flex-grow">
                  {feature.description}
                </p>
                
                <div className="mt-6 flex items-center pt-2 text-sm font-medium opacity-0 group-hover:opacity-100 transform translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                  <span className={`${feature.textColor} relative after:absolute after:w-0 after:h-0.5 after:bottom-0 after:left-0 after:bg-current group-hover:after:w-full after:transition-all after:duration-300`}>
                    En savoir plus
                  </span>
                  <svg className={`ml-1 w-4 h-4 ${feature.textColor} transform group-hover:translate-x-1 transition-transform duration-300`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
